// Свободные окна дня, проверка записи, накладки, повторы.
import { addDays, addMonthsDate, hmToMin, minToHm } from './dates.ts';
import { plural } from './format.ts';
import { STATUS, occupies, type Status } from './status.ts';

// Рабочий день в минутах; обед может быть не задан. null или on=false — выходной.
export interface WorkDay { on: boolean; start: number; end: number; lunchStart?: number | null; lunchEnd?: number | null }
export interface TypeLike { name?: string; dur: number; brk: number; isDefault?: boolean; active?: boolean }
// Сессия дня для расчёта окон: начало и длительность в минутах.
export interface DaySession { id?: string; start: number; dur: number; brk?: number; status: Status; name?: string }
export interface Busy { start: number; end: number }

export interface FreeWindow {
  fs: number;                  // начало свободного времени
  fe: number;                  // конец
  next: 'sess' | 'lunch' | 'busy' | 'end';   // что идёт после окна (перед сессией нужен перерыв)
  fits: TypeLike[];            // какие типы помещаются, от длинного к короткому
  cap: number;                 // сколько стандартных сессий помещается
  step: number;
}

function hasLunch(w: WorkDay): w is WorkDay & { lunchStart: number; lunchEnd: number } {
  return w.lunchStart !== null && w.lunchStart !== undefined && w.lunchEnd !== null && w.lunchEnd !== undefined &&
    w.lunchEnd > w.lunchStart;
}

// opts.nowMin — сегодня окна до «сейчас» не показываются; opts.busy — личные дела из других календарей
// (занимают время как обед, перерыв после них не нужен).
export function freeWindows(work: WorkDay | null, sessions: readonly DaySession[], types: readonly TypeLike[],
  opts: { nowMin?: number | null; step?: number; busy?: readonly Busy[] } = {}): FreeWindow[] {
  if (!work || !work.on) return [];
  const active = types.filter((t) => t.active !== false);
  const std = active.find((t) => t.isDefault) ?? active[0] ?? { dur: 50, brk: 15 };
  const step = opts.step || 5;
  type Ev = [start: number, end: number, kind: 'sess' | 'lunch' | 'busy', brk: number];
  const ev: Ev[] = [];
  for (const s of sessions) if (occupies(s.status)) ev.push([s.start, s.start + s.dur, 'sess', s.brk ?? 0]);
  if (hasLunch(work)) ev.push([work.lunchStart, work.lunchEnd, 'lunch', 0]);
  for (const b of opts.busy ?? []) if (b.end > b.start) ev.push([b.start, b.end, 'busy', 0]);
  ev.sort((a, b) => a[0] - b[0]);
  const gaps: Array<[number, number, FreeWindow['next']]> = [];
  let cur = work.start, curBreak = 0;
  for (const [s, e, kind, brk] of ev) {
    const fs = cur + curBreak;
    if (s > fs) gaps.push([fs, s, kind]);
    cur = Math.max(cur, e);
    curBreak = kind === 'sess' ? brk : 0;
  }
  if (work.end > cur + curBreak) gaps.push([cur + curBreak, work.end, 'end']);
  const out: FreeWindow[] = [];
  for (const [fs0, fe, next] of gaps) {
    let fs = fs0;
    if (opts.nowMin !== undefined && opts.nowMin !== null) {
      if (fe <= opts.nowMin) continue;
      fs = Math.max(fs, opts.nowMin);
    }
    const beforeSess = next === 'sess';
    const fits = active.filter((t) => t.dur + (beforeSess ? t.brk : 0) <= fe - fs).sort((a, b) => b.dur - a.dur);
    if (!fits.length) continue;
    let cap = 0;
    while ((cap + 1) * std.dur + cap * std.brk <= fe - fs - (beforeSess ? std.brk : 0)) cap++;
    out.push({ fs, fe, next, fits, cap, step });
  }
  return out;
}

// Времена начала для типа внутри окна с шагом step.
export function windowStarts(w: FreeWindow, type: TypeLike, step?: number): number[] {
  const st = step || w.step || 5, need = type.dur + (w.next === 'sess' ? type.brk : 0), out: number[] = [];
  for (let t = w.fs; t + need <= w.fe; t += st) out.push(t);
  return out;
}

// «50, 30 или 90»
export function fitsLabel(fits: readonly TypeLike[]): string {
  const d = fits.map((t) => t.dur);
  if (!d.length) return '';
  if (d.length === 1) return String(d[0]);
  return d.slice(0, -1).join(', ') + ' или ' + d[d.length - 1];
}

// Можно ли поставить сессию start/dur/brk в этот день: '' — можно, иначе причина.
export function checkSlot(work: WorkDay | null, sessions: readonly DaySession[], start: number, dur: number,
  brk: number, ignoreId?: string): string {
  if (!work || !work.on) return 'выходной';
  if (start < work.start || start + dur > work.end) return 'вне рабочего времени';
  if (hasLunch(work) && start < work.lunchEnd && start + dur > work.lunchStart) return 'обед';
  for (const s of sessions) {
    if (!occupies(s.status) || (ignoreId && s.id === ignoreId)) continue;
    const a = s.start, b = s.start + s.dur;
    if (start < b + (s.brk ?? 0) && start + dur + (brk || 0) > a) {
      if (start < b && start + dur > a) return 'занято: ' + (s.name || 'сессия') + ' ' + minToHm(a) + '–' + minToHm(b);
      return 'нужен перерыв рядом с сессией ' + minToHm(a) + '–' + minToHm(b);
    }
  }
  return '';
}

// Сколько стандартных сессий помещается во все окна дня.
export function dayCapacity(windows: readonly FreeWindow[]): number {
  return windows.reduce((k, w) => k + w.cap, 0);
}

export interface Conflict { date: string; start: string; a: string; b: string; aName?: string; bName?: string }

// ЧП: две запланированные сессии пересекаются по времени. Перерывы не считаем — только сами сессии.
export function findConflicts(sessions: ReadonlyArray<{ id: string; date: string; start: string; dur?: number;
  status: Status; clientName?: string }>, fromDate?: string): Conflict[] {
  const byDate = new Map<string, typeof sessions[number][]>(), out: Conflict[] = [];
  for (const x of sessions) {
    if (x.status !== STATUS.PLAN || (fromDate && x.date < fromDate)) continue;
    const list = byDate.get(x.date) ?? [];
    list.push(x);
    byDate.set(x.date, list);
  }
  for (const d of [...byDate.keys()].sort()) {
    const list = (byDate.get(d) ?? []).map((x) => { const s = hmToMin(x.start) ?? 0; return { x, s, e: s + (Number(x.dur) || 50) }; })
      .sort((a, b) => a.s - b.s);
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const A = list[i]!, B = list[j]!;
        if (B.s >= A.e) break;
        out.push({ date: d, start: B.x.start, a: A.x.id, b: B.x.id, aName: A.x.clientName, bName: B.x.clientName });
      }
    }
  }
  return out;
}

// Повторы записи: раз в N недель или раз в месяц (то же число; 31-е → последний день месяца).
export type Every = number | 'month';

export function repeatDate(iso: string, every: Every, i: number): string {
  return every === 'month' ? addMonthsDate(iso, i) : addDays(iso, 7 * (Number(every) || 1) * i);
}

export function repeatLabel(every: Every): string {
  if (every === 'month') return 'раз в месяц';
  const n = Number(every) || 1;
  return n === 1 ? 'каждую неделю' : 'раз в ' + n + ' ' + plural(n, 'неделю', 'недели', 'недель');
}
